
import React from 'react';
import { Customer, Quote } from '../types';

interface FinanceDashboardProps {
  customers: Customer[];
  quotes: Quote[];
  onSyncZoho: () => void;
  isSyncing: boolean;
}

const FinanceDashboard: React.FC<FinanceDashboardProps> = ({ customers, quotes, onSyncZoho, isSyncing }) => {
  const totalOutstanding = customers.reduce((sum, c) => sum + c.outstandingBalance, 0);
  const revenuePotential = quotes.reduce((sum, q) => sum + q.totalUSD, 0);
  const totalMargin = quotes.reduce((sum, q) => sum + (q.pnl?.margin || 0), 0);

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in duration-500 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Finance Overview</h2>
          <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Global P&L and Credit Compliance</p>
        </div>
        <div className="flex gap-4">
          <button className="px-6 py-3 bg-white border border-slate-100 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all shadow-sm">Audit Log</button>
          <button 
            onClick={onSyncZoho}
            disabled={isSyncing}
            className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl transition-all flex items-center gap-3 ${
              isSyncing ? 'bg-slate-200 text-slate-400' : 'bg-indigo-600 text-white shadow-indigo-100 hover:bg-indigo-700'
            }`}
          >
            {isSyncing ? (
              <>
                <svg className="animate-spin h-4 w-4 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Syncing Zoho...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                Sync Zoho Books
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <FinanceCard label="Portfolio AR Exposure" value={`$${Number(totalOutstanding || 0).toLocaleString()}`} color="indigo" sub="Cumulative Customer Balance" />
        <FinanceCard label="Pipeline Gross Yield" value={`$${Number(revenuePotential || 0).toLocaleString()}`} color="emerald" sub="Combined Value of Open Quotes" />
        <FinanceCard label="Est. Net Margin Rate" value={revenuePotential > 0 ? `${(totalMargin / revenuePotential * 100).toFixed(1)}%` : '0%'} color="amber" sub="Operational Overhead Efficiency" />
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-[0_8px_40px_rgb(0,0,0,0.03)] border border-slate-50 overflow-hidden">
        <div className="px-10 py-8 border-b border-slate-50 flex justify-between items-center bg-white">
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em]">Zoho Books Credit Gateway</h3>
            <div className="flex items-center gap-2">
               <div className={`w-2 h-2 rounded-full ${isSyncing ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500'}`}></div>
               <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{isSyncing ? 'Updating Data' : 'Sync Active'}</span>
            </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50/50 text-slate-400 text-[10px] uppercase font-black tracking-widest border-b border-slate-50">
              <tr>
                <th className="px-10 py-6">Account Name</th>
                <th className="px-10 py-6">Source</th>
                <th className="px-10 py-6">Limit</th>
                <th className="px-10 py-6">DUE / AGEING</th>
                <th className="px-10 py-6">Auth Status</th>
                <th className="px-10 py-6 text-right">Gate Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-bold">
              {customers.map(c => (
                <tr key={c.id} className="hover:bg-slate-50/30 transition-colors group">
                  <td className="px-10 py-7">
                    <p className="text-slate-800 text-sm tracking-tight">{c.name}</p>
                    <p className="text-[10px] text-slate-300 font-mono mt-1">{c.id}</p>
                  </td>
                  <td className="px-10 py-7">
                    <span className={`text-[8px] font-black px-2 py-0.5 rounded border uppercase tracking-widest ${c.source === 'ZOHO' ? 'border-indigo-200 text-indigo-500 bg-indigo-50' : 'border-slate-200 text-slate-400 bg-slate-50'}`}>
                      {c.source || 'LOCAL'}
                    </span>
                  </td>
                  <td className="px-10 py-7 text-sm text-slate-500 font-mono tracking-tighter">${Number(c.creditLimit || 0).toLocaleString()}</td>
                  <td className="px-10 py-7">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-mono tracking-tighter ${c.outstandingBalance > c.creditLimit ? 'text-rose-600' : 'text-slate-800'}`}>
                        ${Number(c.outstandingBalance || 0).toLocaleString()}
                      </span>
                      {c.outstandingBalance > c.creditLimit && (
                        <div className="w-4 h-4 text-rose-500">
                          <svg fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-10 py-7">
                    <span className={`text-[9px] px-3 py-1 rounded-full font-black uppercase tracking-[0.2em] ${
                      c.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                    }`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-10 py-7 text-right">
                    <button className="text-[10px] font-black text-indigo-500 hover:text-indigo-700 uppercase tracking-widest transition-colors opacity-0 group-hover:opacity-100">Send Notice</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-slate-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
        <div className="flex justify-between items-center relative z-10">
            <div>
                <h4 className="font-black text-2xl tracking-tight mb-2">Yield Analysis Insight</h4>
                <p className="text-slate-400 text-sm max-w-xl font-medium leading-relaxed">
                  Dash-8 fleet utilization is peaking at 85%. Secondary fuel surcharges for HKW (Kogatende) sectors should be reviewed due to increased logistical costs in the high season.
                </p>
            </div>
            <button className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] hover:bg-indigo-500 transition-all uppercase tracking-[0.2em] shadow-lg shadow-indigo-900">
               Generate Yield Report
            </button>
        </div>
      </div>
    </div>
  );
};

const FinanceCard: React.FC<{ label: string; value: string; color: string; sub: string }> = ({ label, value, color, sub }) => {
  const styles = {
    indigo: 'border-l-indigo-600 bg-indigo-50/30',
    emerald: 'border-l-emerald-600 bg-emerald-50/30',
    amber: 'border-l-amber-600 bg-amber-50/30',
  };
  return (
    <div className={`bg-white p-8 rounded-[2rem] border-l-[6px] border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.01)] hover:shadow-[0_10px_30px_rgb(0,0,0,0.03)] transition-all ${styles[color as keyof typeof styles]}`}>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">{label}</p>
      <p className="text-3xl font-black text-slate-900 tracking-tighter mb-2">{value}</p>
      <p className="text-[10px] text-slate-400 font-bold tracking-tight">{sub}</p>
    </div>
  );
};

export default FinanceDashboard;
